import React from "react";
import { Link } from "react-router-dom";
import { Printer } from "lucide-react";
import TopBar from "../components/shop/TopBar";
import Logo from "../components/Logo";
import { InteriorPortrait, ExplodedAssembly } from "../components/shop/InteriorDrawings";
import {
  DIM, PROBES, STRIP_A, STRIP_B, STRIP_C, STRIP_LOGIC, STRIP_PROMPT,
  VARIANTS, WORKFLOW, SERVICE, GRP_META, TESTER_IMG, CONTACT, ROADMAP, COMMERCIAL,
} from "../data/build";
import { PARTS, INTERIOR_NOTES } from "../data/interior";
import { OutreachEmail } from "../components/shop/OutreachEmail";

const LINKS = [
  { to: "/shop", label: "Pool Shop" },
  { to: "/shop/build", label: "Build Spec" },
  { to: "/shop/interior", label: "Interior" },
  { to: "/shop/brief", label: "Brief / RFQ" },
  { to: "/software", label: "Software" },
  { to: "/home", label: "Home System" },
];

const REV = "Rev A · concept stage";
const STRIPS = [STRIP_A, STRIP_B, STRIP_C];

const Label = ({ children }) => (
  <p className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-sky-700">{children}</p>
);
const H = ({ n, children }) => (
  <div className="flex items-baseline gap-3 border-b border-slate-300 pb-2 mb-4">
    <span className="font-mono2 text-sm font-bold text-sky-600">{n}</span>
    <h2 className="font-display text-2xl font-bold text-slate-900">{children}</h2>
  </div>
);

const Page = ({ children }) => (
  <section className="brief-page bg-white rounded-xl border border-slate-200 shadow-sm px-8 py-9 md:px-12 md:py-12 mb-6">
    {children}
  </section>
);

const Foot = ({ p }) => (
  <div className="mt-10 pt-3 border-t border-slate-200 flex justify-between font-mono2 text-[9px] uppercase tracking-widest text-slate-400">
    <span>Aqualux Nexus · Pool Shop Water Tester · Manufacturing Brief &amp; RFQ</span>
    <span>{REV} · Page {p}</span>
  </div>
);

const KV = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-slate-100">
    <dt className="text-slate-500 text-sm">{k}</dt>
    <dd className="text-slate-900 text-sm font-medium text-right">{v}</dd>
  </div>
);

export default function ShopBrief() {
  const today = new Date().toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" });
  const grpParts = Object.keys(GRP_META).map((g) => ({
    g, meta: GRP_META[g], items: PARTS.filter((p) => p.grp === g),
  })).filter((x) => x.items.length);

  return (
    <div className="min-h-screen bg-slate-100" data-testid="shop-brief-page">
      <div className="no-print"><TopBar links={LINKS} /></div>

      {/* Toolbar */}
      <div className="no-print sticky top-0 z-20 bg-slate-900 text-white">
        <div className="mx-auto max-w-[900px] px-5 h-14 flex items-center justify-between">
          <span className="font-mono2 text-xs uppercase tracking-widest text-slate-300 hidden sm:inline">Manufacturing Brief / RFQ · print or save as PDF</span>
          <div className="flex items-center gap-2">
            <Link to="/shop/teaser" data-testid="brief-to-teaser-btn" className="inline-flex items-center gap-2 rounded-full border border-white/20 hover:bg-white/10 text-white text-sm font-semibold px-4 py-2 transition-colors">One-page teaser</Link>
            <button onClick={() => window.print()} data-testid="brief-print-btn" className="inline-flex items-center gap-2 rounded-full bg-sky-500 hover:bg-sky-400 text-white text-sm font-semibold px-5 py-2 transition-colors">
              <Printer className="h-4 w-4" /> Print / Save PDF
            </button>
          </div>
        </div>
      </div>

      {/* Outreach email (screen only — not part of the printed RFQ) */}
      <div className="no-print mx-auto max-w-[900px] px-4 md:px-6 pt-6">
        <OutreachEmail />
      </div>

      <div className="brief-doc mx-auto max-w-[900px] px-4 md:px-6 py-8">

        {/* PAGE 1 — COVER */}
        <Page>
          <div className="flex items-center justify-between">
            <Logo />
            <span className="font-mono2 text-[10px] text-slate-400">{REV}</span>
          </div>
          <div className="mt-3"><Label>Confidential · for review by prospective manufacturing partners</Label></div>
          <div className="mt-16 md:mt-24">
            <p className="font-mono2 text-sm text-sky-600 uppercase tracking-widest">Manufacturing Brief &amp; Request for Quote</p>
            <h1 className="mt-3 font-display text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
              Aqualux Nexus<br />Pool Shop Water Tester
            </h1>
            <p className="mt-5 text-lg text-slate-600 max-w-2xl">
              A benchtop / portable pool-water analyser built on four continuous probes and three top-loaded optical
              test-strip slots — no liquid reagents. We are seeking a development partner to build the first working prototype.
            </p>

            <div className="mt-6 rounded-2xl border-l-4 border-sky-500 bg-slate-50 p-5" data-testid="brief-invitation">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.18em] text-sky-700">Invitation to partner</p>
              <p className="mt-2 text-slate-700 leading-relaxed">
                I am seeking a product-build partner with a genuine <strong>vested interest in the long-term potential</strong> of
                bringing this product to market. Aqualux Nexus targets a worldwide, <strong>multi-billion-dollar industry</strong> —
                pool shops, pool-service technicians, and consumers seeking pool automation and simplicity. The ideal partner has the
                resources to create the <strong>first operational prototypes</strong> and then progress to <strong>full robotic
                production-line manufacturing</strong> — engineering out human error so that every finished unit is delivered to
                <strong> 100% quality control, without compromise</strong>.
              </p>
            </div>
          </div>

          <div className="mt-10 rounded-2xl overflow-hidden border border-slate-200">
            <img src={TESTER_IMG.front} alt="Pool Shop tester — front render" className="w-full object-cover" />
          </div>

          <div className="mt-8 grid sm:grid-cols-2 gap-x-10 gap-y-1">
            <KV k="Prepared by" v={`${CONTACT.company} — ${CONTACT.name}`} />
            <KV k="Contact" v={`${CONTACT.email} · ${CONTACT.phone}`} />
            <KV k="Location" v={CONTACT.location} />
            <KV k="Date" v={today} />
            <KV k="Document" v={CONTACT.docRef} />
          </div>
          <Foot p="1" />
        </Page>

        {/* PAGE 2 — OVERVIEW & WHAT WE'RE ASKING FOR */}
        <Page>
          <H n="1">Project overview</H>
          <p className="text-slate-700 leading-relaxed">
            The Pool Shop Water Tester is the in-store / field member of a four-product ecosystem (Shop, Mobile, Home
            Automation, and a central Pool Software). A customer's water sample is poured into the unit; four continuous
            probes and up to three optical test strips return a full water-chemistry panel in roughly 60–90 seconds, which
            syncs to the Pool Software and prints as a white-label customer report. There are no liquid reagents, cuvettes
            or single-use disks — only inexpensive 6&nbsp;mm test strips (and the unit runs probe-only if no strip is loaded).
          </p>

          <div className="mt-6">
            <Label>Headline specification</Label>
            <dl className="mt-2">
              <KV k="Envelope (proposed)" v={`${DIM.w} × ${DIM.h} × ${DIM.d} mm`} />
              <KV k="Weight" v={DIM.weight} />
              <KV k="Display" v={DIM.screen} />
              <KV k="Probes" v={DIM.probes} />
              <KV k="Strip slots" v={DIM.strips} />
              <KV k="Battery" v={DIM.battery} />
              <KV k="Panel time" v={DIM.panel} />
              <KV k="Reagents" v="None — no liquids, cuvettes or cartridges" />
            </dl>
          </div>

          <div className="mt-6 rounded-2xl border-2 border-sky-200 bg-sky-50/60 p-5">
            <Label>What we are asking a partner to quote</Label>
            <ol className="mt-3 space-y-2 text-sm text-slate-700 list-decimal pl-5">
              <li><strong>Feasibility review</strong> of this brief (probes, optical strip reading, fluidics, self-clean, self-calibration).</li>
              <li>A single <strong>"looks-like / works-like" proof-of-concept prototype</strong> of the Countertop variant.</li>
              <li>Indicative path and cost to a <strong>DFM design</strong> and a <strong>small pilot batch (5–10 units)</strong>.</li>
              <li>A rough <strong>target unit cost</strong> estimate at ~100 and ~1,000 unit volumes.</li>
            </ol>
            <p className="mt-3 text-xs text-slate-500">We can sign an NDA before sharing further internals. All design IP produced under contract is to be owned by Aqualux Nexus.</p>
          </div>

          <div className="mt-6">
            <Label>A phased build path — room to grow together</Label>
            <div className="mt-3 grid sm:grid-cols-3 gap-3">
              {ROADMAP.map((r) => (
                <div key={r.step} className="rounded-2xl border border-slate-200 p-4" data-testid={`brief-roadmap-${r.step}`}>
                  <div className="flex items-center gap-2">
                    <span className="h-7 w-7 rounded-full bg-slate-900 text-white flex items-center justify-center font-mono2 text-xs font-bold shrink-0">{r.step}</span>
                    <p className="font-display font-bold text-slate-900 text-sm leading-tight">{r.name}</p>
                  </div>
                  <p className="mt-2 text-xs text-slate-600">{r.d}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-5" data-testid="brief-commercial">
            <Label>Commercial intent</Label>
            <p className="mt-2 text-sm text-slate-700">{COMMERCIAL.rollout}</p>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {COMMERCIAL.bands.map((b) => (
                <div key={b.k} className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-center">
                  <p className="font-mono2 text-[9px] uppercase tracking-widest text-slate-400">{b.k}</p>
                  <p className="font-display font-bold text-slate-900 text-sm">{b.v}</p>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-slate-500">{COMMERCIAL.bandsNote}</p>
            <p className="mt-1 text-xs text-slate-500">{COMMERCIAL.price}</p>
          </div>
          <Foot p="2" />
        </Page>

        {/* PAGE 3 — TWO VARIANTS */}
        <Page>
          <H n="2">Two build variants (identical measurement core)</H>
          <p className="text-slate-700">The two variants share every measurement subassembly and differ only in how rinse water and power are handled. The proof-of-concept should target the Countertop variant first.</p>
          <div className="mt-6 grid sm:grid-cols-2 gap-4">
            {VARIANTS.map((v) => (
              <div key={v.key} className="rounded-2xl border border-slate-200 p-5" data-testid={`brief-variant-${v.key}`}>
                <div className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full" style={{ background: v.color }} />
                  <p className="font-display text-lg font-bold text-slate-900">{v.name}</p>
                </div>
                <p className="font-mono2 text-[10px] uppercase tracking-widest text-slate-400 mt-0.5">{v.tag}</p>
                <dl className="mt-3">
                  <KV k="Rinse" v={v.rinse} />
                  <KV k="Waste" v={v.waste} />
                  <KV k="Power" v={v.power} />
                </dl>
                <p className="mt-3 text-sm text-slate-600">{v.note}</p>
              </div>
            ))}
          </div>
          <div className="mt-6 rounded-2xl overflow-hidden border border-slate-200">
            <img src={TESTER_IMG.rear} alt="Pool Shop tester — rear I/O render" className="w-full object-cover" />
          </div>
          <Foot p="3" />
        </Page>

        {/* PAGE 4 — FUNCTIONAL: PROBES + STRIPS */}
        <Page>
          <H n="3">Functional requirements — what it measures</H>
          <Label>Continuous probe block (no consumables)</Label>
          <table className="mt-2 w-full text-sm">
            <thead><tr className="text-left font-mono2 text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-200">
              <th className="py-2 pr-3">#</th><th className="py-2 pr-3">Parameter</th><th className="py-2">Sensing method</th>
            </tr></thead>
            <tbody>
              {PROBES.map((p) => (
                <tr key={p.p} className="border-b border-slate-100">
                  <td className="py-2 pr-3 font-mono2 text-slate-400">{p.p}</td>
                  <td className="py-2 pr-3 font-medium text-slate-900">{p.name}</td>
                  <td className="py-2 text-slate-600">{p.method}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <Label>Three top-loaded 6&nbsp;mm optical strip slots</Label>
          <div className="mt-2 space-y-3">
            {STRIPS.map((s) => (
              <div key={s.port} className="rounded-xl border border-slate-200 p-4">
                <p className="font-semibold text-slate-900">Slot {s.port} — {s.label}</p>
                <ul className="mt-1.5 grid sm:grid-cols-2 gap-x-6 gap-y-1">
                  {s.reads.map((r) => (
                    <li key={r.t} className="text-sm text-slate-600"><span className="text-slate-900 font-medium">{r.t}</span> — {r.note}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <Foot p="4" />
        </Page>

        {/* PAGE 5 — DETECTION & PROMPT LOGIC */}
        <Page>
          <H n="4">Smart strip detection &amp; guided prompts</H>
          <Label>Detection logic — the unit reports exactly what is loaded (1–3 strips)</Label>
          <dl className="mt-2 mb-6">
            {STRIP_LOGIC.map((x) => <KV key={x.case} k={x.case} v={x.out} />)}
          </dl>

          <Label>Profile-driven prompt LEDs — an indicator in front of each slot lights the strips to insert</Label>
          <table className="mt-2 w-full text-sm">
            <thead><tr className="text-left font-mono2 text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-200">
              <th className="py-2 pr-3">Client pool profile</th><th className="py-2 pr-3">Slots lit</th><th className="py-2">Why</th>
            </tr></thead>
            <tbody>
              {STRIP_PROMPT.map((x) => (
                <tr key={x.profile} className="border-b border-slate-100">
                  <td className="py-2 pr-3 font-medium text-slate-900">{x.profile}</td>
                  <td className="py-2 pr-3">
                    {x.lights.map((l) => <span key={l} className="inline-block mr-1 rounded-full bg-sky-100 text-sky-700 font-mono2 text-[10px] px-2 py-0.5">{l}</span>)}
                  </td>
                  <td className="py-2 text-slate-600">{x.note}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-4 text-xs text-slate-500">The profile lookup lives in the Pool Software / client database; the strip-present sensors independently confirm the actual strips loaded before reporting.</p>
          <Foot p="5" />
        </Page>

        {/* PAGE 6 — MECHANICAL / FLUIDICS */}
        <Page>
          <H n="5">Mechanical, fluidics &amp; serviceability</H>
          <ul className="space-y-3 text-slate-700 text-sm leading-relaxed">
            {INTERIOR_NOTES.map((n, i) => (
              <li key={n} className="flex gap-3"><span className="font-mono2 text-sky-600 text-xs mt-0.5">{String(i + 1).padStart(2, "0")}</span><span>{n}</span></li>
            ))}
          </ul>
          <div className="mt-5 rounded-2xl border border-sky-200 bg-sky-50/60 p-4">
            <Label>Maintenance &amp; calibration (key requirement)</Label>
            <p className="mt-2 text-sm text-slate-700">{SERVICE.replace("Maintenance & calibration: ", "")}</p>
          </div>
          <div className="mt-6 rounded-2xl border border-slate-200 p-4">
            <Label>Internal general arrangement (front elevation, cover removed)</Label>
            <div className="mt-2 overflow-x-auto"><InteriorPortrait /></div>
          </div>
          <Foot p="6" />
        </Page>

        {/* PAGE 7 — WORKFLOW */}
        <Page>
          <H n="6">Operator workflow (self-cleaning between customers)</H>
          <ol className="space-y-4">
            {WORKFLOW.map((s, i) => (
              <li key={s.t} className="flex gap-4">
                <span className="shrink-0 h-8 w-8 rounded-full bg-slate-900 text-white flex items-center justify-center font-mono2 text-sm font-bold">{i + 1}</span>
                <div><p className="font-display font-bold text-slate-900">{s.t}</p><p className="text-sm text-slate-600 mt-0.5">{s.d}</p></div>
              </li>
            ))}
          </ol>
          <Foot p="7" />
        </Page>

        {/* PAGE 8 — ELECTRONICS + SOFTWARE */}
        <Page>
          <H n="7">Electronics, firmware &amp; connectivity</H>
          <ul className="space-y-2 text-sm text-slate-700">
            <li><strong>MCU / control:</strong> ARM Cortex-M class controller running the measurement sequencing, chemistry engine, on-device storage, screen host and comms.</li>
            <li><strong>Analog front-end:</strong> low-noise conditioning for the four probes plus LED drivers + photodiode capture for the three strip readers.</li>
            <li><strong>Display / status:</strong> 5" capacitive touchscreen for the operator UI, plus an RGB status light strip (Good / Adjusting / Attention) and three per-slot prompt LEDs.</li>
            <li><strong>Connectivity:</strong> USB-C and Bluetooth LE to the shop PC / Pool Software for result sync and branded report printing.</li>
            <li><strong>Power:</strong> mains on the countertop unit; 14.8&nbsp;V Li-ion (~6&nbsp;h) with charge management on the portable unit.</li>
          </ul>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-4">
            <Label>Software sync (already built)</Label>
            <p className="mt-2 text-sm text-slate-700">The Pool Software exists and already exposes a device ingestion endpoint (test results posted per client, keyed by a device key). The unit's firmware needs to package a completed test and POST it on USB/BT/WiFi — no server work required from the hardware partner.</p>
          </div>
          <div className="mt-6 rounded-2xl border border-slate-200 p-4">
            <Label>Exploded assembly (numbered to the component schedule)</Label>
            <div className="mt-2 overflow-x-auto"><ExplodedAssembly /></div>
          </div>
          <Foot p="8" />
        </Page>

        {/* PAGE 9+ — COMPONENT SCHEDULE / BOM */}
        <Page>
          <H n="8">Component schedule (proposed BOM)</H>
          <p className="text-slate-600 text-sm mb-4">Numbering matches the exploded assembly and the Interior pack. Sizes/materials are proposed engineering intent to validate in DFM.</p>
          {grpParts.map(({ g, meta, items }) => (
            <div key={g} className="mb-5 break-inside-avoid">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: meta.color }} />
                <p className="font-display font-bold text-slate-900 text-sm">{meta.label}</p>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="text-left font-mono2 text-[9px] uppercase tracking-widest text-slate-400 border-b border-slate-200">
                  <th className="py-1.5 pr-2 w-6">#</th><th className="py-1.5 pr-2">Component</th><th className="py-1.5 pr-2">Proposed size</th><th className="py-1.5 pr-2">Material</th><th className="py-1.5 pr-2 w-8">Qty</th>
                </tr></thead>
                <tbody>
                  {items.map((p) => (
                    <tr key={p.n} className="border-b border-slate-100 align-top">
                      <td className="py-1.5 pr-2 font-mono2 text-slate-400">{p.n}</td>
                      <td className="py-1.5 pr-2"><span className="font-medium text-slate-900">{p.name}</span><br /><span className="text-xs text-slate-500">{p.fn}</span></td>
                      <td className="py-1.5 pr-2 text-slate-600 text-xs">{p.size}</td>
                      <td className="py-1.5 pr-2 text-slate-600 text-xs">{p.material}</td>
                      <td className="py-1.5 pr-2 text-slate-700">{p.qty}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          <Foot p="9" />
        </Page>

        {/* FINAL PAGE — WHAT TO INCLUDE IN YOUR QUOTE */}
        <Page>
          <H n="9">Information to include in your response</H>
          <p className="text-slate-700 text-sm mb-4">To help us compare partners quickly, please address the following in your proposal:</p>
          <ol className="space-y-2.5 text-sm text-slate-700 list-decimal pl-5">
            <li>Relevant experience with <strong>analytical instruments, sensors/probes, optical detection and small-scale fluidics</strong> (portfolio examples).</li>
            <li>Your view on <strong>technical feasibility and the top risks</strong> (probe calibration/stability, strip-read repeatability, self-clean carry-over).</li>
            <li>Proposed <strong>phases, timeline and cost</strong> to a single working proof-of-concept.</li>
            <li>Indicative path and cost to <strong>DFM + a 5–10 unit pilot</strong>, and a rough <strong>unit cost at ~100 and ~1,000 units</strong>.</li>
            <li>Which parts you would <strong>source as off-the-shelf modules</strong> (probes, pumps, screen) vs. custom.</li>
            <li>Team, location, and how you handle <strong>IP ownership and NDAs</strong>.</li>
          </ol>

          <div className="mt-8 grid sm:grid-cols-2 gap-6">
            <div>
              <Label>Reference material available on request</Label>
              <ul className="mt-2 text-sm text-slate-600 space-y-1 list-disc pl-5">
                <li>Full dimensioned Build Spec (exterior + GA + cutaway)</li>
                <li>Interior pack (portrait / landscape / aerial / exploded drawings)</li>
                <li>Per-component catalog with schematic glyphs</li>
                <li>Pool Software &amp; the device ingestion API</li>
              </ul>
            </div>
            <div>
              <Label>Contact</Label>
              <dl className="mt-2">
                <KV k="Company" v={CONTACT.company} />
                <KV k="Contact name" v={CONTACT.name} />
                <KV k="Email" v={CONTACT.email} />
                <KV k="Phone" v={CONTACT.phone} />
                <KV k="Location" v={CONTACT.location} />
              </dl>
            </div>
          </div>

          <p className="mt-10 text-xs text-slate-400 border-t border-slate-200 pt-4">
            All figures, dimensions, materials and component choices in this document are concept-stage engineering intent and are to be
            validated during feasibility, prototyping and DFM. This document is confidential and shared for the purpose of quotation only.
          </p>
          <Foot p="10" />
        </Page>

      </div>
    </div>
  );
}
